import { Injectable } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications';
import { Storage } from '@ionic/storage-angular';
import { NotificacionService } from '../notificaciones-service/notificacion.service';
import { ApiService } from 'src/app/api.service';
import { LocalNotifications } from '@capacitor/local-notifications';
import { AppPlatformService } from '../platform/app-platform.service';
import { NotificationsActivitiesService } from '../calendar-service/notifications-activities.service';
import { Subject } from 'rxjs';
import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { NotificationStatus } from './notification-status.plugin';

@Injectable({
  providedIn: 'root'
})
export class PushNotificationsService {

  // Emite cuando se debe mostrar el modal de "activar notificaciones manualmente"
  public showManualEnableModal$ = new Subject<void>();
  // Listener de un solo uso para detectar el retorno desde la pantalla de ajustes
  private settingsReturnListener?: PluginListenerHandle;
  // Evita que varias llamadas (flujo de login + auto-login) abran el modal por duplicado
  private manualEnablePromptPending = false;
  // Latch: una vez mostrado el modal en esta apertura de la app, no se vuelve a mostrar.
  // A diferencia de manualEnablePromptPending (que se libera en finally y solo cubre
  // llamadas concurrentes), esto evita que una segunda llamada posterior (>3s) reabra
  // el modal al terminar de drenar la cadena de modales.
  private manualEnablePromptShown = false;

  constructor(public platform: Platform,
    private Storage: Storage,
    private ns: NotificacionService,
    private api: ApiService,
    private appPlatformService:AppPlatformService,
    private notificationsActivitiesService: NotificationsActivitiesService,
    private modalController: ModalController,
  ) {
    if (this.appPlatformService.isAppPlatform()){
      this.initializeNotificationEvents();
    }
  }

  private initializeNotificationEvents() {
     // On success, we should be able to receive notifications
     PushNotifications.addListener('registration',
      (token: Token) => {
        this.api.guardar_token_fcm(token.value)
        .then(() => {console.log("token fcm enviado!")} )
        .catch(error => {console.log("error en envío de token fcm:", error)} );

      }
    );

    // Some issue with our setup and push will not work
    PushNotifications.addListener('registrationError',
      (error: any) => {
        console.log("error al registrar token fcm",  JSON.stringify(error));
      }
    );

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener('pushNotificationReceived',
      async (notification: PushNotificationSchema) => {
        // alert('Push received when APP IS OPEN: ' + JSON.stringify(notification));
        console.log('Push received when APP IS OPEN', notification);
        // Mostrar la notificación localmente para que el usuario la vea
        try {
          await this.showLocalNotification(notification);
        } catch (error) {
          console.log(error);
        };

        this.notificationsActivitiesService.handleNotificationUpdate(true, true, 1);
      }
    );

    // Method called when tapping on a notification
    PushNotifications.addListener('pushNotificationActionPerformed',
      (notification: ActionPerformed) => {
        // alert('Push action performed: ' + JSON.stringify(notification));
        console.log('Push action performed: ' + JSON.stringify(notification));
      }
    );

  }

  public async requestPermissions() {
    console.log("request notificacion permissions! ");
    try {
      let status = await PushNotifications.checkPermissions();

      if (status.receive !== 'granted') {
        status = await PushNotifications.requestPermissions();
      }

      if (status.receive === 'granted') {
        console.log('granted');
        PushNotifications.register();
      } else {
        console.log('requestPermissions not granted:', status.receive);
      }
    } catch (error) {
      console.log('error en requestPermissions:', error);
    }
  }

  /**
   * Solicita el permiso de notificaciones apenas se abre la app.
   * Solo lanza el prompt nativo si el permiso aún no fue decidido (evita re-preguntar).
   * No registra el token aquí; el registro se hace en el flujo de login.
   */
  public async requestPermissionsOnAppOpen(): Promise<void> {
    if (!this.appPlatformService.isAppPlatform()) return;
    try {
      const status = await PushNotifications.checkPermissions();
      if (status.receive === 'prompt' || status.receive === 'prompt-with-rationale') {
        await PushNotifications.requestPermissions();
      }
    } catch (error) {
      console.log("error en requestPermissionsOnAppOpen:", error);
    }
  }

  /**
   * 2 segundos después de ingresar a la app, si el permiso está denegado y no hay
   * ningún modal en pantalla (espera a que se cierre la cadena de modales abiertos
   * -ej. modal de actualización o de recompensas-), emite para mostrar el modal que
   * redirige a los ajustes del dispositivo.
   */
  public promptEnableNotificationsIfDenied(): void {
    if (!this.appPlatformService.isAppPlatform()) return;
    // Ya se mostró (o está por mostrarse) el modal en esta apertura de la app: no
    // reabrir. Cubre llamadas separadas en el tiempo (login vs. auto-login) que la
    // guarda de concurrencia no atrapa porque se libera en el finally.
    if (this.manualEnablePromptShown) return;
    // Si ya hay una verificación en curso (login + auto-login pueden llamar casi a
    // la vez), se ignora la llamada duplicada para no abrir el modal dos veces.
    if (this.manualEnablePromptPending) return;
    this.manualEnablePromptPending = true;
    setTimeout(async () => {
      try {
        const status = await PushNotifications.checkPermissions();
        const enabled = await this.areNotificationsEnabledNative();
        console.log("promptEnableNotificationsIfDenied", status.receive, "enabled:", enabled)

        // Casos en los que mostramos el modal que redirige a Ajustes:
        // - 'denied' / 'prompt-with-rationale': Android 13+ con permiso denegado.
        // - 'granted' pero el SO reporta notificaciones deshabilitadas: Android < 13,
        //   donde checkPermissions() siempre devuelve "granted" y el único modo de
        //   detectar la desactivación manual es areNotificationsEnabled().
        const deniedByPermission = status.receive === 'denied' || status.receive === 'prompt-with-rationale';
        const manuallyDisabled = status.receive === 'granted' && !enabled;
        if (!deniedByPermission && !manuallyDisabled) return;

        // Se marca antes de drenar la cadena de modales: mientras esperamos que el
        // usuario cierre los modales abiertos, cualquier llamada posterior queda
        // bloqueada por el latch y no emitirá un segundo modal.
        this.manualEnablePromptShown = true;

        let top = await this.modalController.getTop();
        while (top) {
          await top.onDidDismiss();
          top = await this.modalController.getTop();
        }

        this.showManualEnableModal$.next();
      } catch (error) {
        console.log("error en promptEnableNotificationsIfDenied:", error);
      } finally {
        this.manualEnablePromptPending = false;
      }
    }, 3000);
  }

  /**
   * Consulta al plugin nativo si las notificaciones están habilitadas a nivel del SO.
   * Necesario en Android < 13, donde PushNotifications.checkPermissions() siempre
   * devuelve "granted". Solo aplica a Android; en iOS/errores devuelve true para no
   * mostrar el modal de ajustes por equivocación (ahí manda checkPermissions()).
   */
  private async areNotificationsEnabledNative(): Promise<boolean> {
    if (!this.platform.is('android')) return true;
    try {
      const { enabled } = await NotificationStatus.areEnabled();
      return enabled;
    } catch (error) {
      console.log("error en areNotificationsEnabledNative:", error);
      return true;
    }
  }

  /**
   * Abre los ajustes del dispositivo para que el usuario active las notificaciones
   * manualmente. Previamente arma un listener de un solo uso que registra el token
   * FCM cuando el usuario vuelve a la app habiendo concedido el permiso.
   */
  public async openNotificationSettings(): Promise<void> {
    try {
      await this.registerSettingsReturnListener();
      if (this.platform.is('ios')) {
        await NativeSettings.openIOS({ option: IOSSettings.App });
      } else {
        await NativeSettings.openAndroid({ option: AndroidSettings.AppNotification });
      }
    } catch (error) {
      console.log("error al abrir ajustes de notificaciones:", error);
    }
  }

  private async registerSettingsReturnListener(): Promise<void> {
    if (this.settingsReturnListener) return;
    this.settingsReturnListener = await App.addListener('appStateChange', async (state) => {
      if (!state.isActive) return;
      try {
        const status = await PushNotifications.checkPermissions();
        const enabled = await this.areNotificationsEnabledNative();
        // En Android < 13 checkPermissions() siempre es "granted"; exigimos también
        // que el SO reporte las notificaciones habilitadas antes de registrar el token.
        if (status.receive === 'granted' && enabled) {
          // Dispara el listener 'registration' -> api.guardar_token_fcm
          PushNotifications.register();
        }
      } catch (error) {
        console.log("error al verificar permiso tras volver de ajustes:", error);
      }
      await this.settingsReturnListener?.remove();
      this.settingsReturnListener = undefined;
    });
  }

  async showLocalNotification(notification: PushNotificationSchema) {
    try {
      // Pedir permisos para notificaciones locales
      const permResult = await LocalNotifications.requestPermissions();

      if (permResult.display === 'granted') {
        await LocalNotifications.schedule({
          notifications: [{
            title: notification.title,
            body: notification.body,
            id: Date.now(),
            sound: 'default'
          }]
        });
      };

    } catch (error) {
      throw error
    }
  }
}
